import React, { useState } from 'react';
import type { LocalizedString } from '../../types';
import { Locale } from '../../types';
import { translateFields } from '../../services/adminApi';
import type { TranslatableFieldKind } from '../../services/adminApi';
import styles from './AdminCrud.module.css';

interface LocalizedInputProps {
  label: string;
  /** Field name sent to the translator, e.g. 'title' or 'pros' */
  field_key: string;
  value: LocalizedString;
  onChange: (value: LocalizedString) => void;
  textarea?: boolean;
  /** 'array' fields are edited as one item per line and translated line by line */
  kind?: TranslatableFieldKind;
}

const TRANSLATED_LOCALES: { key: Locale; label: string }[] = [
  { key: Locale.en, label: 'EN' },
  { key: Locale.kz, label: 'KZ' },
];

const to_lines = (text: string): string[] =>
  text.split('\n').map((line) => line.trim()).filter(Boolean);

/**
 * Russian is the only field the admin fills in. "Перевести" sends it to
 * Claude Haiku via /api/admin/translate and fills EN/KZ, which stay editable
 * behind the "показать переводы" toggle.
 */
const LocalizedInput: React.FC<LocalizedInputProps> = ({
  label,
  field_key,
  value,
  onChange,
  textarea = false,
  kind = 'string',
}) => {
  const [showTranslations, setShowTranslations] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle_change = (locale: Locale, text: string) => {
    onChange({ ...value, [locale]: text });
  };

  const handle_translate = async () => {
    setError(null);
    setIsTranslating(true);

    try {
      const ru = kind === 'array' ? to_lines(value.ru) : value.ru;
      const { translations } = await translateFields([{ key: field_key, kind, ru }]);
      const translated = translations[field_key];

      if (!translated) {
        setError('Перевод не получен');
        return;
      }

      const as_text = (item: string | string[]): string =>
        Array.isArray(item) ? item.join('\n') : item;

      onChange({
        ...value,
        [Locale.en]: as_text(translated.en),
        [Locale.kz]: as_text(translated.kz),
      });
      setShowTranslations(true);
    } catch (err) {
      setError((err as Error).message || 'Не удалось перевести');
    } finally {
      setIsTranslating(false);
    }
  };

  const render_field = (locale: Locale, placeholder: string) =>
    textarea ? (
      <textarea
        className={styles.textarea}
        placeholder={placeholder}
        value={value[locale] ?? ''}
        onChange={(event) => handle_change(locale, event.target.value)}
      />
    ) : (
      <input
        className={styles.input}
        placeholder={placeholder}
        value={value[locale] ?? ''}
        onChange={(event) => handle_change(locale, event.target.value)}
      />
    );

  return (
    <div className={styles.field}>
      <span className={styles.field_label}>{label} (RU)</span>
      {render_field(Locale.ru, 'Введите текст на русском')}

      <div className={styles.translate_row}>
        <button
          type="button"
          className={`${styles.button_secondary} ${styles.button_small}`}
          onClick={handle_translate}
          disabled={isTranslating || !value.ru.trim()}
        >
          {isTranslating ? 'Перевод…' : 'Перевести'}
        </button>
        <button
          type="button"
          className={styles.toggle_link}
          onClick={() => setShowTranslations((shown) => !shown)}
        >
          {showTranslations ? 'Скрыть переводы' : 'Показать переводы (EN / KZ)'}
        </button>
      </div>

      {error && <p className={styles.error_text}>{error}</p>}

      {showTranslations && (
        <div className={styles.localized_group}>
          {TRANSLATED_LOCALES.map(({ key, label: locale_label }) => (
            <div key={key} className={styles.field}>
              <span className={styles.field_label}>{locale_label}</span>
              {render_field(key, locale_label)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LocalizedInput;
